import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DATETIME, MONTHS, MONTH_NAMES, buildMonthGrid, isInRange, makeDayKey, todayKey } from '../../utils/datePickerUtils';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const YEARS_PER_PAGE = 12;
const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

const cx = (...names) => names.filter(Boolean).join(' ');

/** First month to show: the selection, else today kept inside min and max. */
const initialView = (selected, min, max) => {
  const today = todayKey();
  let start = selected || today;
  if (!selected && min && today < min) start = min;
  if (!selected && max && today > max) start = max;
  return { year: Number(start.slice(0, 4)), month: Number(start.slice(5, 7)) - 1 };
};

const TimeColumn = ({ label, options, selected, disabled, onPick }) => (
  <div className="ems-dp__time-col" role="group" aria-label={label}>
    {options.map((option) => (
      <button
        key={option}
        type="button"
        className={cx('ems-dp__time-option', option === selected && 'is-selected')}
        disabled={disabled}
        aria-pressed={option === selected}
        ref={option === selected ? (el) => { if (el) el.parentElement.scrollTop = el.offsetTop - el.parentElement.offsetTop - 48; } : undefined}
        onClick={() => onPick(option)}
      >
        {option}
      </button>
    ))}
  </div>
);

/** The calendar. `selected` is a value in the picker's variant; the parent decides what picks do. */
const DatePickerPanel = ({ panelRef, style, variant, selected, min, max, problem, onPickDay, onPickTime, onToday, onClear, onDone }) => {
  const [view, setView] = useState(() => initialView(selected, min, max));
  const [mode, setMode] = useState('days');
  const { year, month } = view;

  const selectedDay = selected.slice(0, 10);
  const [hour, minute] = selected ? (selected.slice(11, 16) || '00:00').split(':') : [];
  const today = todayKey();
  // Keeps the years on both sides of the current one in view.
  const pageStart = year - 4;

  const showMonth = (delta) => {
    const next = new Date(year, month + delta, 1);
    setView({ year: next.getFullYear(), month: next.getMonth() });
  };
  const step = (direction) => {
    if (mode === 'days') showMonth(direction);
    if (mode === 'months') setView({ year: year + direction, month });
    if (mode === 'years') setView({ year: year + direction * YEARS_PER_PAGE, month });
  };

  const canStep = (direction) => {
    if (mode === 'days') {
      const next = new Date(year, month + direction, 1);
      return isInRange(makeDayKey(next.getFullYear(), next.getMonth(), 1).slice(0, 7), min, max);
    }
    const target = mode === 'months' ? year + direction : pageStart + (direction > 0 ? YEARS_PER_PAGE : -1);
    return isInRange(String(target), min, max);
  };

  return (
    <div
      ref={panelRef}
      className={cx('ems-dp', variant === DATETIME && 'ems-dp--datetime')}
      style={style}
      role="dialog"
      aria-label={variant === DATETIME ? 'Choose date and time' : 'Choose date'}
      // Keeps focus in the input so typing can carry on. The time columns still need their scrollbar.
      onMouseDown={(e) => { if (!e.target.classList.contains('ems-dp__time-col')) e.preventDefault(); }}
    >
      <div className="ems-dp__main">
        <div className="ems-dp__head">
          <button type="button" className="ems-dp__nav" aria-label="Previous" disabled={!canStep(-1)} onClick={() => step(-1)}>
            <ChevronLeft size={16} />
          </button>
          <div className="ems-dp__title">
            <button type="button" className={cx('ems-dp__title-btn', mode === 'months' && 'is-active')} onClick={() => setMode(mode === 'months' ? 'days' : 'months')}>
              {MONTH_NAMES[month]}
            </button>
            <button type="button" className={cx('ems-dp__title-btn', mode === 'years' && 'is-active')} onClick={() => setMode(mode === 'years' ? 'days' : 'years')}>
              {year}
            </button>
          </div>
          <button type="button" className="ems-dp__nav" aria-label="Next" disabled={!canStep(1)} onClick={() => step(1)}>
            <ChevronRight size={16} />
          </button>
        </div>

        {mode === 'days' && (
          <div className="ems-dp__days">
            {WEEKDAYS.map((name) => <span key={name} className="ems-dp__weekday">{name}</span>)}
            {buildMonthGrid(year, month).map(({ key, day, outside }) => (
              <button
                key={key}
                type="button"
                className={cx('ems-dp__day', outside && 'is-outside', key === today && 'is-today', key === selectedDay && 'is-selected')}
                disabled={!isInRange(key, min, max)}
                aria-label={key}
                aria-pressed={key === selectedDay}
                onClick={() => onPickDay(key)}
              >
                {day}
              </button>
            ))}
          </div>
        )}

        {mode === 'months' && (
          <div className="ems-dp__grid">
            {MONTHS.map((name, index) => (
              <button
                key={name}
                type="button"
                className={cx('ems-dp__cell', index === month && 'is-selected')}
                disabled={!isInRange(makeDayKey(year, index, 1).slice(0, 7), min, max)}
                onClick={() => { setView({ year, month: index }); setMode('days'); }}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {mode === 'years' && (
          <div className="ems-dp__grid">
            {Array.from({ length: YEARS_PER_PAGE }, (_, i) => pageStart + i).map((option) => (
              <button
                key={option}
                type="button"
                className={cx('ems-dp__cell', option === year && 'is-selected')}
                disabled={!isInRange(String(option), min, max)}
                onClick={() => { setView({ year: option, month }); setMode('months'); }}
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {problem && <p className="ems-dp__problem" role="alert">{problem}</p>}

        <div className="ems-dp__foot">
          <button type="button" className="ems-dp__link" onClick={onClear}>Clear</button>
          {!min && !max && <button type="button" className="ems-dp__link" onClick={onToday}>Today</button>}
          {variant === DATETIME && <button type="button" className="ems-dp__done" onClick={onDone}>Done</button>}
        </div>
      </div>

      {variant === DATETIME && (
        <div className="ems-dp__time">
          <TimeColumn label="Hour" options={HOURS} selected={hour} disabled={!selected} onPick={(h) => onPickTime(`${h}:${minute}`)} />
          <TimeColumn label="Minute" options={MINUTES} selected={minute} disabled={!selected} onPick={(m) => onPickTime(`${hour}:${m}`)} />
        </div>
      )}
    </div>
  );
};

export default DatePickerPanel;
